import React, { useState, useEffect } from 'react'
import {
  Container, Paper, Typography, Box, Button, Alert, CircularProgress, Stack,
} from '@mui/material'
import { gql, useQuery, useMutation } from '@apollo/client'
import PayoutConfigForm from '../components/PayoutConfig/PayoutConfigForm'
import EntityPicker from '../components/PayoutConfig/EntityPicker'
import {
  getRiderPayoutOverride,
  saveRiderPayoutOverride,
  clearRiderPayoutOverride,
} from '../apollo/mutations'
import { getRiders } from '../apollo/queries'

const GET = gql`${getRiderPayoutOverride}`
const SAVE = gql`${saveRiderPayoutOverride}`
const CLEAR = gql`${clearRiderPayoutOverride}`
const RIDERS = gql`${getRiders}`

const NUM_FIELDS = [
  'opt1HourlyRate', 'opt2HourlyRate', 'opt2PerOrderBonus',
  'opt3FlatFeePerOrder', 'opt4BaseFee', 'opt4PerMileRate',
  'opt5PercentOfDeliveryFee', 'opt6MinimumPerDelivery', 'opt8BonusPerExtraOrder',
]
const BOOL_FIELDS = [
  'opt1Enabled', 'opt2Enabled', 'opt3Enabled', 'opt4Enabled',
  'opt5Enabled', 'opt6Enabled', 'opt8Enabled',
]
function pickInput(cfg) {
  const out = {}
  BOOL_FIELDS.forEach(k => { if (cfg?.[k] != null) out[k] = !!cfg[k] })
  NUM_FIELDS.forEach(k => {
    if (cfg?.[k] != null && cfg[k] !== '') {
      const n = parseFloat(cfg[k])
      if (Number.isFinite(n)) out[k] = n
    }
  })
  return out
}

function driverTypeLabel(t) {
  if (t === 'RUSHMYFOOD') return 'Platform driver'
  if (t === 'RESTAURANT') return 'Staff driver'
  return t || 'Driver'
}

export default function RiderPayoutOverride() {
  const initialId = new URLSearchParams(window.location.search).get('riderId') || ''
  const [riderId, setRiderId] = useState(initialId)

  const { data: ridersData, loading: ridersLoading } = useQuery(RIDERS, {
    fetchPolicy: 'cache-and-network',
  })
  const riders = ridersData?.riders || []

  const { data, loading, error, refetch } = useQuery(GET, {
    variables: { riderId },
    skip: !riderId,
    fetchPolicy: 'cache-and-network',
  })
  const [cfg, setCfg] = useState({})
  const [saved, setSaved] = useState('')
  const [save, { loading: saving, error: saveError }] = useMutation(SAVE, {
    onCompleted: () => { setSaved('Override saved.'); refetch() },
  })
  const [clearMut, { loading: clearing }] = useMutation(CLEAR, {
    onCompleted: () => { setSaved('Override cleared.'); setCfg({}); refetch() },
  })

  useEffect(() => {
    if (data?.riderPayoutOverride) setCfg(data.riderPayoutOverride)
    else if (data) setCfg({})
  }, [data])

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Per-Rider Payout Override
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Optional override that wins over the scope-level (platform or restaurant) config
          for one specific rider — useful for star drivers who deserve a bumped rate. Any field
          set here replaces the scope value; unset fields fall back to scope.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <EntityPicker
            options={riders}
            value={riderId}
            onChange={setRiderId}
            loading={ridersLoading}
            label="Rider"
            placeholder="Pick a rider"
            getLabel={(o) => `${o?.name || 'Unnamed'} · ${driverTypeLabel(o?.driverType)}`}
            getSubLabel={(o) => o?.username || o?.phone || `Rider #${o?._id}`}
          />
        </Box>

        {riderId && loading && !data && <CircularProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError.message}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved('')}>{saved}</Alert>}

        {riderId && (
          <>
            <PayoutConfigForm value={cfg} onChange={setCfg} disabled={saving || clearing} />
            <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                disabled={saving || clearing}
                onClick={() => { setSaved(''); clearMut({ variables: { riderId } }) }}
              >
                {clearing ? 'Clearing…' : 'Clear override'}
              </Button>
              <Button
                variant="contained"
                disabled={saving || clearing}
                onClick={() => {
                  setSaved('')
                  save({ variables: { riderId, input: pickInput(cfg) } })
                }}
              >
                {saving ? 'Saving…' : 'Save override'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Container>
  )
}
